import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const ShopContext = createContext(null);

export const FREE_SHIPPING_THRESHOLD = 2500;
export const STANDARD_SHIPPING = 99;
export const PRIORITY_SHIPPING = 249;

export function ShopProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("riaas_cart") || "[]");
    } catch {
      return [];
    }
  });

  const refreshProducts = useCallback(async () => {
    try {
      const r = await api.get("/products");
      setProducts(r.data);
    } catch (e) {
      console.error("Failed to load products", e);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const refreshCategories = useCallback(async () => {
    try {
      const r = await api.get("/categories");
      setCategories(r.data);
    } catch (e) {
      console.error("Failed to load categories", e);
    }
  }, []);

  useEffect(() => {
    refreshProducts();
    refreshCategories();
  }, [refreshProducts, refreshCategories]);

  useEffect(() => {
    localStorage.setItem("riaas_cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = useCallback((product, qty = 1) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.id === product.id ? { ...i, qty: Math.min(i.qty + qty, product.stock) } : i
        );
      }
      return [
        ...prev,
        { id: product.id, name: product.name, price: product.price, image: product.image, stock: product.stock, qty: Math.min(qty, product.stock) },
      ];
    });
  }, []);

  const updateQty = useCallback((id, qty) => {
    setCart((prev) =>
      qty <= 0 ? prev.filter((i) => i.id !== id) : prev.map((i) => (i.id === id ? { ...i, qty } : i))
    );
  }, []);

  const removeFromCart = useCallback((id) => {
    setCart((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
    setCoupon(null);
  }, []);

  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);
  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);
  const discount = useMemo(
    () => (coupon ? Math.round((subtotal * coupon.discount_pct) / 100) : 0),
    [coupon, subtotal]
  );

  return (
    <ShopContext.Provider
      value={{
        products,
        categories,
        loadingProducts,
        refreshProducts,
        refreshCategories,
        cart,
        cartOpen,
        setCartOpen,
        addToCart,
        updateQty,
        removeFromCart,
        clearCart,
        cartCount,
        subtotal,
        discount,
        coupon,
        setCoupon,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
}

export const useShop = () => useContext(ShopContext);
